// src/components/settings/shared/SettingsTableActions.tsx
import React from 'react';
import { Box, Tooltip } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  DoNotDisturbAlt as DoNotDisturbIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { SettingsEditButton, SettingsDeleteButton } from './SettingsElements';

interface SettingsTableActionsProps {
  itemId: number;
  itemName: string;
  refCount: number;
  onEdit: () => void;
  onDelete: () => void;
  refLabel?: string;
  stopPropagation?: boolean;
  editColor?: string;
  deleteColor?: string;
  disabledDeleteColor?: string;
  // True for standard/seeded reference data - Delete is locked with its own
  // reason and icon, distinct from the refCount-based lock below.
  isSystem?: boolean;
}

const SettingsTableActions: React.FC<SettingsTableActionsProps> = ({
  itemName,
  refCount,
  onEdit,
  onDelete,
  refLabel = 'reference',
  stopPropagation = false,
  editColor,
  deleteColor,
  disabledDeleteColor,
  isSystem = false,
}) => {
  const hasRefs = refCount > 0;
  const deleteDisabled = hasRefs || isSystem;

  const deleteTooltip = isSystem
    ? 'Standard system item - cannot be deleted'
    : hasRefs
      ? `Has ${refCount} active ${refLabel}${refCount !== 1 ? 's' : ''}`
      : 'Delete';

  const handleEdit = (e?: React.MouseEvent) => {
    if (stopPropagation && e) e.stopPropagation();
    onEdit();
  };

  const handleDelete = (e?: React.MouseEvent) => {
    if (stopPropagation && e) e.stopPropagation();
    onDelete();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1,
        pr: '1rem',
        '@media (max-width: 600px)': {
          gap: 0.02,
          pr: 0,
        },
      }}
    >
      <SettingsEditButton
        onClick={(e) => handleEdit(e)}
        aria-label={`edit ${itemName}`}
        sx={editColor ? { color: editColor } : undefined}
      >
        <EditIcon />
      </SettingsEditButton>
      {/* <Tooltip title={deleteTooltip}> */}
      <span>
        <SettingsDeleteButton
          disabled={deleteDisabled}
          onClick={(e) => handleDelete(e)}
          aria-label={`delete ${itemName}`}
          sx={{
            ...(deleteColor && { color: deleteColor }),
            ...(disabledDeleteColor && {
              '&.Mui-disabled': { color: disabledDeleteColor },
            }),
          }}
        >
          {isSystem ? (
            <LockIcon />
          ) : hasRefs ? (
            <DoNotDisturbIcon />
          ) : (
            <DeleteIcon />
          )}
        </SettingsDeleteButton>
      </span>
      {/* </Tooltip> */}
    </Box>
  );
};

export default SettingsTableActions;
